import { Directive, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, Renderer2, DestroyRef } from '@angular/core';
import { merge, Subject, throttleTime } from 'rxjs';
import { filter } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Directive({
  selector: '[displayingElementsNumber]',
})
export class DisplayingElementsNumberDirective implements OnInit, OnDestroy {
  private resizeObserver: ResizeObserver;

  private childrenObserver: MutationObserver;

  private readonly elementWidthChange$ = new Subject<number>();

  private readonly elementDomChange$ = new Subject<number>();

  @Input()
  elementsGap = 0;

  @Input()
  throttleTime = 200;

  @Input()
  manipulateItemsDisplaying = true;

  @Output()
  numberOfDisplayedElements = new EventEmitter<number>();

  constructor(
    private readonly elementRef: ElementRef<HTMLElement>,
    private readonly renderer: Renderer2,
    private readonly destroyRef: DestroyRef,
  ) {}

  ngOnInit(): void {
    merge(this.elementWidthChange$.pipe(filter(Boolean), throttleTime(this.throttleTime)), this.elementDomChange$.pipe(filter(Boolean)))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(this.changeNumberOfElements.bind(this));

    this.initializeRelativeElement();
    this.initializeListenerForSizeChanges();
    this.initializeListenerForDomChanges();
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.childrenObserver?.disconnect();
  }

  private initializeListenerForSizeChanges(): void {
    this.resizeObserver = new ResizeObserver(() => {
      const availableWidth = this.getAvailableWidth();
      this.elementWidthChange$.next(availableWidth);
    });

    // we need to observe parent element width (wrapper)
    this.resizeObserver.observe(this.elementRef.nativeElement.parentElement);
  }

  private initializeListenerForDomChanges(): void {
    this.childrenObserver = new MutationObserver((records: MutationRecord[]) => {
      const wereChildrenModified = records.some((record: MutationRecord) => {
        return record.addedNodes.length || record.removedNodes.length;
      });

      if (wereChildrenModified) {
        const availableWidth = this.getAvailableWidth();
        this.elementDomChange$.next(availableWidth);
      }
    });

    this.childrenObserver.observe(this.elementRef.nativeElement, {
      attributes: false,
      childList: true,
      subtree: false,
    });
  }

  private initializeRelativeElement(): void {
    this.renderer.addClass(this.elementRef.nativeElement, 'relative');
  }

  private changeNumberOfElements(availableWidth: number): void {
    const numberOfElements = this.getNumberOfDisplayedElements(availableWidth);

    if (this.manipulateItemsDisplaying) {
      this.toggleElementsDisplaying(numberOfElements);
    }

    this.numberOfDisplayedElements.emit(numberOfElements);
  }

  private getAvailableWidth(): number {
    const parentElement = this.elementRef.nativeElement.parentElement;

    // take other elements except the one that this directive is on
    const otherElements = Array.from(parentElement.children as any as HTMLElement[])
      .filter((element: HTMLElement) => element !== this.elementRef.nativeElement);

    // calculate overall width of other children elements in wrapper
    const sumOfOtherElementsWidth = otherElements.reduce((result: number, element: HTMLElement) => {
      return element.getBoundingClientRect().width + result;
    }, 0);

    // calculate available by subtraction sum of other children width from wrapper width
    const availableWidth = parentElement.getBoundingClientRect().width - sumOfOtherElementsWidth;

    return availableWidth;
  }

  private getNumberOfDisplayedElements(availableWidth: number): number {
    const childrenElements = [...this.elementRef.nativeElement.children as any as HTMLElement[]];
    let totalWidth = 0;
    let numberOfItems = 0;

    for (let i = 0; i < childrenElements.length; i++) {
      const usedWidth = totalWidth + childrenElements[i].offsetWidth;

      if (usedWidth <= availableWidth) {
        totalWidth = usedWidth + this.elementsGap;
        numberOfItems = i + 1;
      } else {
        break;
      }
    }

    return numberOfItems;
  }

  private toggleElementsDisplaying(numberOfDisplayedElements: number): void {
    const childrenElements = [...this.elementRef.nativeElement.children as any as HTMLElement[]];

    childrenElements.forEach((element: HTMLElement, index: number) => {
      if ((index + 1) > numberOfDisplayedElements) {
        this.hideElement(element);
      } else {
        this.showElement(element);
      }
    });
  }

  private hideElement(element: HTMLElement): void {
    this.renderer.addClass(element, 'absolute');
    this.renderer.setStyle(element, 'z-index', -100);
  }

  private showElement(element: HTMLElement): void {
    this.renderer.removeClass(element, 'absolute');
    this.renderer.removeStyle(element, 'z-index');
  }
}
